import {
  writeBatch,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  DocumentData,
  CollectionReference,
  DocumentReference,
  WriteBatch,
  Query,
  QuerySnapshot
} from 'firebase/firestore';
import { User } from 'firebase/auth';
import { auth, db } from '@lib/firebase';

import { mapFirebaseError, ApiError } from '../errors/ApiError';
import loggingService from '../../services/loggingService';
import * as validators from '../../utils/api/validators';

import cacheService from './CacheService';

// Define a filter interface
export interface Filter {
  field: string;
  operator: '==' | '>' | '<' | '>=' | '<=' | 'in' | 'array-contains';
  value: any;
}

export interface ServiceContext {
  [key: string]: any;
}

export interface DocumentWithId extends DocumentData {
  id: string;
}

class ServiceBase {
  protected serviceName: string;
  protected validate: typeof validators;
  protected cache: typeof cacheService;

  constructor(serviceName: string) {
    this.serviceName = serviceName;
    this.validate = validators;
    this.cache = cacheService;
  }

  getCurrentUser(): User {
    const user = auth?.currentUser;
    if (!user) {
      throw new ApiError(
        'AUTH_ERROR',
        'User must be authenticated to perform this operation',
        null
      );
    }
    return user;
  }

  getCurrentUserId(): string {
    return this.getCurrentUser().uid;
  }

  async getDoc<T extends DocumentData = DocumentData>(collectionPath: string, docId: string): Promise<T & { id: string }> {
    try {
      const docRef = doc(db, collectionPath, docId);
      const docSnapshot = await getDoc(docRef);

      if (!docSnapshot.exists()) {
        throw new ApiError(
          'NOT_FOUND',
          `Document not found: ${collectionPath}/${docId}`,
          null
        );
      }

      return {
        id: docSnapshot.id,
        ...docSnapshot.data()
      } as T & { id: string };
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw mapFirebaseError(error);
    }
  }

  async getCollection<T extends DocumentData = DocumentData>(
    collectionPath: string | CollectionReference | Query,
    filters: Filter[] = []
  ): Promise<(T & { id: string })[]> {
    try {
      let queryRef: Query | CollectionReference;

      if (typeof collectionPath === 'string') {
        const segments = collectionPath.split('/');
        if (segments.length % 2 === 1) {
          queryRef = collection(db, collectionPath);
        } else {
          // It's a subcollection path that might be formatted as doc path?
          // Actually if segments % 2 == 0, it's a DOCUMENT path.
          // But implementation says: doc(db, collectionPath).parent -> which is the collection.
          // This seems odd logic in original, but preserving strict logic:
          queryRef = doc(db, collectionPath).parent;
        }
      } else {
        queryRef = collectionPath;
      }

      const querySnapshot: QuerySnapshot = await getDocs(queryRef);
      // Explicitly type the result of map
      let docs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as (T & { id: string })[];

      if (filters && filters.length > 0) {
        docs = this._applyFilters(docs, filters);
      }

      return docs;
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw mapFirebaseError(error);
    }
  }

  // Memo: This performs Client-Side filtering!
  // This is legacy behavior from the original file.
  protected _applyFilters<T extends DocumentData>(docs: (T & { id: string })[], filters: Filter[]): (T & { id: string })[] {
    return docs.filter(doc => {
      return filters.every(filter => {
        const { field, operator, value } = filter;
        const docValue = doc[field as keyof (T & { id: string })];

        switch (operator) {
          case '==':
            return docValue === value;
          case '>':
            return docValue > value;
          case '<':
            return docValue < value;
          case '>=':
            return docValue >= value;
          case '<=':
            return docValue <= value;
          case 'in':
            return Array.isArray(value) && value.includes(docValue);
          case 'array-contains':
            return Array.isArray(docValue) && docValue.includes(value);
          default:
            return true;
        }
      });
    });
  }

  async setDoc<T extends DocumentData = DocumentData>(collectionPath: string, docId: string, data: T): Promise<T & { id: string }> {
    try {
      const docRef = doc(db, collectionPath, docId);
      await setDoc(docRef, data, { merge: false });
      return { id: docId, ...data };
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw mapFirebaseError(error);
    }
  }

  async updateDoc<T extends DocumentData = DocumentData>(collectionPath: string, docId: string, updates: Partial<T>): Promise<{ id: string } & Partial<T>> {
    try {
      const docRef = doc(db, collectionPath, docId);
      await updateDoc(docRef, updates as DocumentData); // Type annotation needed for firebase
      return { id: docId, ...updates };
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw mapFirebaseError(error);
    }
  }

  async deleteDoc(collectionPath: string, docId: string): Promise<{ id: string; deleted: boolean }> {
    try {
      const docRef = doc(db, collectionPath, docId);
      await deleteDoc(docRef);
      return { id: docId, deleted: true };
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw mapFirebaseError(error);
    }
  }

  batch(): WriteBatch {
    return writeBatch(db);
  }

  log(message: string, context: ServiceContext = {}): void {
    loggingService.info(`[${this.serviceName}] ${message}`, context);
  }

  logError(error: any, context: ServiceContext = {}): void {
    const errorContext = {
      ...context,
      serviceName: this.serviceName,
      errorCode: error?.code || 'UNKNOWN'
    };
    loggingService.error(error, errorContext);
  }

  clearCache(): void {
    this.cache.clear();
  }
}

export default ServiceBase;
